import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { KPICard } from "../ui/kpi-card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "../ui/alert";
import {
  Skeleton,
  SkeletonCard,
  SkeletonTable,
  SkeletonKPICard,
  SkeletonChart,
} from "../ui/skeleton";
import {
  EnhancedTabs,
  EnhancedTabsContent,
  EnhancedTabsList,
  EnhancedTabsTrigger,
} from "../ui/enhanced-tabs";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  CleanTable,
  CleanTableBody,
  CleanTableCell,
  CleanTableHead,
  CleanTableHeader,
  CleanTableRow,
} from "../ui/clean-table";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Building,
  Building2,
  AlertTriangle,
  BarChart3,
  Shield,
  Settings,
  Users,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { useUser } from "../../contexts/UserContext";
import PageHeader from "../Common/PageHeader";

import { BusinessUnitService } from "../../services/businessUnitService";
import { RiskService } from "../../services/riskService";
import { ControlService } from "../../services/controlService";
import { MetadataService } from "../../services/metadataService";
import { getApiUrl } from "../../config/api";
import RiskHeatMap from "../Common/RiskHeatMap";
import RiskCategoryDistribution from "../Common/RiskCategoryDistribution";
import {
  calculateInherentRisk,
  calculateResidualRisk,
  calculateRiskCoverage,
  formatRiskValue,
} from "../../lib/riskAnalytics";

const BusinessUnitDashboard = () => {
  const { businessUnitId } = useParams();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const {
    canManageBusinessUnit,
    isBusinessUnitHead,
    isBusinessUser,
    isOrgAdmin,
  } = useUser();

  const [businessUnit, setBusinessUnit] = useState(null);
  const [risks, setRisks] = useState([]);
  const [controls, setControls] = useState([]);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [myBusinessUnits, setMyBusinessUnits] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [likelihoods, setLikelihoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [risksLoading, setRisksLoading] = useState(false);
  const [controlsLoading, setControlsLoading] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState("risks");

  // Check permissions
  const canManage = canManageBusinessUnit(businessUnitId) || isOrgAdmin;
  const canViewControls = canManage || isBusinessUser;
  const canViewRisks = canManage || isBusinessUnitHead;

  // Set default tab based on permissions
  useEffect(() => {
    if (canViewRisks) {
      setActiveTab("risks");
    } else if (canViewControls) {
      setActiveTab("controls");
    }
  }, []); // Only run once on mount

  const fetchBusinessUnitData = async () => {
    try {
      setLoading(true);

      // First, get my business units to check if I have access to this one
      const myBusinessUnitsData = await BusinessUnitService.getMyBusinessUnits(
        getAccessTokenSilently
      );
      setMyBusinessUnits(myBusinessUnitsData);

      // Check if this business unit is in my list (for business unit heads and business users)
      const hasDirectAccess = myBusinessUnitsData.some(
        (bu) => bu.id === businessUnitId
      );

      let businessUnitData;
      let organizationsData = [];

      if (isOrgAdmin) {
        // Org admins can access any business unit and need organization data
        const [businessUnitResponse, organizationsResponse] = await Promise.all(
          [
            BusinessUnitService.getBusinessUnitDetails(
              businessUnitId,
              getAccessTokenSilently
            ),
            fetch(getApiUrl("organizations"), {
              headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
              },
            }),
          ]
        );

        if (!organizationsResponse.ok) {
          throw new Error("Failed to fetch organizations");
        }

        businessUnitData = businessUnitResponse;
        organizationsData = await organizationsResponse.json();
      } else if (hasDirectAccess) {
        // Business unit heads and users can only access business units they're associated with
        // Find the business unit from my list (which already has the data we need)
        businessUnitData = myBusinessUnitsData.find(
          (bu) => bu.id === businessUnitId
        );

        // If we need more detailed data, make a specific call
        if (!businessUnitData.organization_name) {
          try {
            businessUnitData = await BusinessUnitService.getBusinessUnitDetails(
              businessUnitId,
              getAccessTokenSilently
            );
          } catch (err) {
            // If the detailed call fails, use what we have from my business units
            console.warn(
              "Failed to fetch detailed business unit data, using basic data:",
              err
            );
          }
        }
      } else {
        throw new Error(
          "You don't have permission to access this business unit"
        );
      }

      setUserOrganizations(organizationsData);
      setBusinessUnit(businessUnitData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRisks = async () => {
    if (!businessUnitId || !canViewRisks) return;

    try {
      setRisksLoading(true);
      const data = await RiskService.getRisksForBusinessUnit(
        businessUnitId,
        getAccessTokenSilently
      );
      setRisks(data);
    } catch (err) {
      console.error("Error fetching risks:", err);
      setRisks([]);
    } finally {
      setRisksLoading(false);
    }
  };

  const fetchControls = async () => {
    if (!businessUnitId || !businessUnit || !canViewControls) return;

    try {
      setControlsLoading(true);
      let data;

      if (isBusinessUser) {
        // Business users only see their assigned controls
        const response = await fetch(getApiUrl("controls/my-assigned"), {
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch assigned controls");
        }

        data = await response.json();
        // Filter to only show controls for this business unit
        data = data.filter(
          (control) =>
            control.business_unit_id === businessUnitId ||
            (!control.business_unit_id &&
              control.organization_id === businessUnit.organization_id)
        );
      } else {
        // Business unit heads and org admins see all business unit controls
        data = await ControlService.getControls(
          businessUnit.organization_id,
          businessUnitId,
          getAccessTokenSilently
        );
      }

      setControls(data);
    } catch (err) {
      console.error("Error fetching controls:", err);
      setControls([]);
    } finally {
      setControlsLoading(false);
    }
  };

  const fetchMetadata = async () => {
    if (!businessUnit?.organization_id) return;

    try {
      setMetadataLoading(true);
      const [severitiesData, likelihoodsData] = await Promise.all([
        MetadataService.getSeverities(
          businessUnit.organization_id,
          getAccessTokenSilently
        ),
        MetadataService.getLikelihoods(
          businessUnit.organization_id,
          getAccessTokenSilently
        ),
      ]);
      setSeverities(severitiesData);
      setLikelihoods(likelihoodsData);
    } catch (err) {
      console.error("Error fetching metadata:", err);
      setSeverities([]);
      setLikelihoods([]);
    } finally {
      setMetadataLoading(false);
    }
  };

  useEffect(() => {
    if (businessUnitId) {
      fetchBusinessUnitData();
    }
  }, [businessUnitId, getAccessTokenSilently]);

  useEffect(() => {
    if (businessUnit) {
      fetchRisks();
      fetchControls();
      fetchMetadata();
    }
  }, [businessUnit, canViewRisks, canViewControls, isBusinessUser]);

  const getRiskStats = (risksList) => {
    const stats = {
      total: risksList.length,
      critical: risksList.filter((r) => r.risk_level >= 15).length,
      high: risksList.filter((r) => r.risk_level >= 9 && r.risk_level < 15)
        .length,
      medium: risksList.filter((r) => r.risk_level >= 5 && r.risk_level < 9)
        .length,
      low: risksList.filter((r) => r.risk_level < 5).length,
    };
    return stats;
  };

  if (loading) {
    return (
      <div className="p-10 w-full">
        {/* Header Skeleton */}
        <div className="flex items-center gap-4 mb-8">
          <Skeleton className="h-8 w-64" />
        </div>

        {/* KPI Cards Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonKPICard key={index} />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <SkeletonChart />
          <SkeletonChart />
        </div>

        {/* Tabs Skeleton */}
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <SkeletonCard>
            <div className="space-y-4">
              <Skeleton className="h-6 w-48" />
              <SkeletonTable rows={5} columns={4} />
            </div>
          </SkeletonCard>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 w-full">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!businessUnit) {
    return (
      <div className="p-10 w-full">
        <Alert>
          <AlertDescription>Business unit not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  const riskStats = getRiskStats(risks);

  return (
    <div className="p-10 w-full">
      {/* Header */}
      <PageHeader pageName="Dashboard" entityName={businessUnit?.name} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
        {canViewRisks && (
          <>
            <KPICard
              title="Total Risks"
              value={risks.length}
              icon={AlertTriangle}
              variant="horizontal"
              valueColor="text-foreground"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Inherent"
              value={formatRiskValue(calculateInherentRisk(risks))}
              icon={AlertTriangle}
              variant="horizontal"
              valueColor="text-foreground"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Residual"
              value={formatRiskValue(calculateResidualRisk(risks))}
              icon={Shield}
              variant="horizontal"
              valueColor="text-foreground"
              className="border-2 border-slate-200"
            />
            <KPICard
              title="Coverage"
              value={`${calculateRiskCoverage(risks)}%`}
              icon={BarChart3}
              valueColor="text-foreground"
              variant="horizontal"
              className="border-2 border-slate-200"
            />
          </>
        )}
        {canViewControls && !canViewRisks && (
          <KPICard
            title={isBusinessUser ? "My Controls" : "Total Controls"}
            value={controls.length}
            icon={Shield}
            variant="horizontal"
            valueColor="text-foreground"
            className="border-2 border-slate-200"
          />
        )}
      </div>

      {/* Risk Analytics */}
      {canViewRisks && risks.length > 0 && (
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <RiskHeatMap
            risks={risks}
            severities={severities}
            likelihoods={likelihoods}
            title="Risk Heat Map"
            className="border-2 border-slate-200"
          />
          <RiskCategoryDistribution
            risks={risks}
            title="Risk Category Distribution"
            className="border-2 border-slate-200"
          />
        </div>
      )}

      {/* Loading state for analytics */}
      {canViewRisks && (risksLoading || metadataLoading) && (
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle>Risk Heat Map</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle>Risk Category Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <EnhancedTabs value={activeTab} onValueChange={setActiveTab}>
        <EnhancedTabsList variant="compact">
          {canViewRisks && (
            <EnhancedTabsTrigger value="risks" icon={AlertTriangle}>
              Risks
            </EnhancedTabsTrigger>
          )}
          {canViewControls && (
            <EnhancedTabsTrigger value="controls" icon={Shield}>
              {isBusinessUser ? "My Controls" : "Controls"}
            </EnhancedTabsTrigger>
          )}
        </EnhancedTabsList>

        {canViewRisks && (
          <EnhancedTabsContent value="risks" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Risks Overview</CardTitle>
                  {canManage && (
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(`/business-unit/${businessUnitId}/risks`)
                      }
                      className="flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Manage All
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {risksLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : risks.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No risks have been created for this business unit yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Risks Table */}
                    <div>
                      <CleanTable>
                        <CleanTableHeader>
                          <CleanTableRow>
                            <CleanTableHead>Risk ID</CleanTableHead>
                            <CleanTableHead>Risk Name</CleanTableHead>
                            <CleanTableHead>Category</CleanTableHead>
                            <CleanTableHead className="text-center">
                              Severity
                            </CleanTableHead>
                            <CleanTableHead className="text-center">
                              Likelihood
                            </CleanTableHead>
                          </CleanTableRow>
                        </CleanTableHeader>
                        <CleanTableBody>
                          {risks.slice(0, 5).map((risk) => (
                            <CleanTableRow key={risk.id}>
                              <CleanTableCell>
                                <span className="text-sm font-mono text-gray-600">
                                  {risk.custom_id || risk.id?.slice(0, 8)}
                                </span>
                              </CleanTableCell>
                              <CleanTableCell>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {risk.name ||
                                      risk.description ||
                                      "No name provided"}
                                  </p>
                                  {risk.description &&
                                    risk.name !== risk.description && (
                                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                        {risk.description}
                                      </p>
                                    )}
                                </div>
                              </CleanTableCell>
                              <CleanTableCell>
                                <span className="text-gray-700">
                                  {risk.Category?.name || "Uncategorized"}
                                </span>
                              </CleanTableCell>
                              <CleanTableCell className="text-center">
                                <span className="text-gray-900 font-medium">
                                  {risk.Severity?.value || "N/A"}
                                </span>
                              </CleanTableCell>
                              <CleanTableCell className="text-center">
                                <span className="text-gray-900 font-medium">
                                  {risk.Likelihood?.value || "N/A"}
                                </span>
                              </CleanTableCell>
                            </CleanTableRow>
                          ))}
                        </CleanTableBody>
                      </CleanTable>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </EnhancedTabsContent>
        )}

        {canViewControls && (
          <EnhancedTabsContent value="controls" className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {isBusinessUser ? "My Controls" : "Controls Overview"}
                  </CardTitle>
                  {canManage && (
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(`/business-unit/${businessUnitId}/controls`)
                      }
                      className="flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Manage All
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {controlsLoading ? (
                  <div className="flex justify-center py-8">
                    <Spinner size="lg" />
                  </div>
                ) : controls.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {isBusinessUser
                        ? "No controls have been assigned to you yet."
                        : "No controls have been created for this business unit yet."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Controls Table */}
                    <div>
                      <CleanTable>
                        <CleanTableHeader>
                          <CleanTableRow>
                            <CleanTableHead>Control ID</CleanTableHead>
                            <CleanTableHead>Control Name</CleanTableHead>
                            <CleanTableHead>Category</CleanTableHead>
                            <CleanTableHead>Sub-Category</CleanTableHead>
                            <CleanTableHead className="text-center">
                              Assigned Users
                            </CleanTableHead>
                          </CleanTableRow>
                        </CleanTableHeader>
                        <CleanTableBody>
                          {controls.slice(0, 5).map((control) => (
                            <CleanTableRow key={control.id}>
                              <CleanTableCell>
                                <span className="text-sm font-mono text-gray-600">
                                  {control.custom_id}
                                </span>
                              </CleanTableCell>
                              <CleanTableCell>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {control.name}
                                  </p>
                                </div>
                              </CleanTableCell>
                              <CleanTableCell>
                                <span className="text-gray-700">
                                  {control.SubCategory?.Category?.name ||
                                    control.categories?.[0]?.name ||
                                    control.Category?.name ||
                                    "Uncategorized"}
                                </span>
                              </CleanTableCell>
                              <CleanTableCell>
                                {control.SubCategory ? (
                                  <span className="text-gray-900">
                                    {control.SubCategory.name}
                                  </span>
                                ) : (
                                  <span className="text-gray-500 text-sm">
                                    No sub-category
                                  </span>
                                )}
                              </CleanTableCell>
                              <CleanTableCell className="text-center">
                                <span className="text-sm font-medium">
                                  {control.assigned_users?.length || 0}
                                </span>
                              </CleanTableCell>
                            </CleanTableRow>
                          ))}
                        </CleanTableBody>
                      </CleanTable>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </EnhancedTabsContent>
        )}
      </EnhancedTabs>
    </div>
  );
};

export default BusinessUnitDashboard;
